import { BadgeVariant } from '@/components/Badge/Badge.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import {
    ExternalEventWithMainVariantDataFragment,
    GetExternalEventAllocationUsagesDocument,
    GetExternalEventSeriesWithEventsDataDocument,
} from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed, Ref, toValue } from 'vue';
import { externalEventVariantStatusToBadgeVariant } from '../externalEvent';

const DEFAULT_EXTERNAL_EVENT_PHASE_COLOR = 'External-Event-Category-External-Event';

export function useExternalEventSeriesData(seriesId: Ref<string>) {
    const fluent = useFluent();
    const { result: getExternalEventSeriesWithEventsResult, loading } = useQuery(
        GetExternalEventSeriesWithEventsDataDocument,
        () => ({
            id: toValue(seriesId),
        }),
    );
    const { result: allocationUsages } = useQuery(GetExternalEventAllocationUsagesDocument);

    const series = computed(() => getExternalEventSeriesWithEventsResult.value?.externalEventSeries);

    function mapForMainVariantTiles(event: ExternalEventWithMainVariantDataFragment) {
        const mainVariant = event.mainVariant.nodes[0];
        return {
            ...event,
            mainVariant: {
                id: mainVariant.id,
                eventId: mainVariant.eventId,
                index: mainVariant.index,
                status: mainVariant.status,
                statusBadgeVariant: externalEventVariantStatusToBadgeVariant(mainVariant.status) as BadgeVariant,
                statusBadgeLabel: fluent.$t('external-event-variant-status', { status: mainVariant.status }),
                linkTarget: {
                    name: 'external-event-event-variants-tab',
                    params: { eventId: event.id },
                    hash: `#v${mainVariant.index}`,
                },
                referenceAllocationId: mainVariant.referenceAllocationId ?? undefined,
            },
        };
    }

    type MappedEvent = ReturnType<typeof mapForMainVariantTiles>;

    const externalEvents = computed(() => series.value?.events.nodes.map((e) => mapForMainVariantTiles(e)) ?? []);
    const noExternalEvents = computed(() => externalEvents.value.length === 0);

    const externalEventPhaseTypes = computed(
        () =>
            allocationUsages.value?.externalEventAllocationUsages?.nodes.map((n) => ({
                ...n,
                color: series.value
                    ? mapToPhaseColor(n.type, series.value.category)
                    : DEFAULT_EXTERNAL_EVENT_PHASE_COLOR,
            })) ?? [],
    );

    const currentYear = DateTime.now().year;
    const groupingPeriods = ['past', 'future'] as const;

    const groupedExternalEvents = computed(() => {
        const groups = Object.groupBy(externalEvents.value, (event) => {
            if (event.year < currentYear) {
                return 'past';
            } else {
                return 'future';
            }
        });
        const map = new Map<string, MappedEvent[]>();
        for (const period of groupingPeriods) {
            const sortedObjects =
                groups[period]?.toSorted((a, b) => a.year - b.year || a.nameShort.localeCompare(b.nameShort)) ?? [];
            map.set(period, sortedObjects);
        }
        return map;
    });

    return {
        loading,
        series,
        noExternalEvents,
        externalEventPhaseTypes,
        groupedExternalEvents,
    };
}
